import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { BookOpen, BarChart3, CheckCircle, TrendingUp, Target, Zap, Sparkles, Play } from 'lucide-react';
import { LoadingSpinner } from '../components/LoadingSpinner';
import LandingStyleHero from '../components/productHero/LandingStyleHero';
import ProductDemoVideoEmbed from '../components/video/ProductDemoVideoEmbed';
import { getWalkthroughVideo } from '../config/walkthroughVideos';
import SEO from '../components/SEO';
import PremiumAccessPrompt from '../components/PremiumAccessPrompt';
import { usePremiumProductAccess } from '../hooks/usePremiumProductAccess';
import { PREMIUM_PRODUCT_KEYS } from '../config/premiumProducts';

const sectionTitleClass =
  'text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900 bg-clip-text text-transparent';

const glassCardClass =
  'rounded-2xl border border-slate-200/80 bg-white/95 shadow-lg shadow-slate-200/50 p-5 sm:p-6 md:p-8 transition-all duration-300 hover:shadow-xl hover:border-sky-200/60 hover:-translate-y-0.5';

const whatYouGet = [
  { icon: Target, title: 'Real decision practice', text: 'Make the same kind of trade-offs the assessment forces you to make instead of memorizing a static explanation.' },
  { icon: BookOpen, title: 'Coach mode when you want to learn', text: 'Use guided practice to understand the logic, the math, and the selection rules without feeling lost.' },
  { icon: BarChart3, title: 'Exam-style reps when you want pressure', text: 'Switch to a more test-like flow once you know the basics so your timing and judgment start to hold up under pressure.' },
];

const howItWorks = [
  { icon: BookOpen, title: 'Work through the site requirements', text: 'Read the site information, understand what matters, and decide what kind of trio the situation is really asking for.' },
  { icon: BarChart3, title: 'Select and test your trio', text: 'Choose microbes, balance the attributes, and see whether the trio actually satisfies the site instead of only looking close.' },
  { icon: CheckCircle, title: 'Get coach insights after submit', text: 'Review the checks you passed, the ones you missed, and the one change that would improve the run the most.' },
  { icon: TrendingUp, title: 'Repeat until the logic becomes natural', text: 'The value is in repeated reps. Each run should make the next one feel cleaner, faster, and less guessy.' },
];

const whyThisHelps = [
  { icon: Target, title: 'You stop guessing', text: 'The assessment gets easier when the selection logic stops feeling mysterious.' },
  { icon: BookOpen, title: 'You learn the why, not just the outcome', text: 'Good feedback makes the decision process reusable instead of tied to one scenario.' },
  { icon: TrendingUp, title: 'You build confidence through reps', text: 'The more often you make the decisions yourself, the calmer you will be when the real assessment starts.' },
];

const relatedLinks = [
  { label: 'Sea Wolf Guide & Solver', path: '/seawolf', Icon: BookOpen },
  { label: 'McKinsey AI Coach', path: '/mckinsey-ai-coach', Icon: Sparkles },
  { label: 'Red Rock Cases', path: '/redrock', Icon: BookOpen },
  { label: 'About McKinsey Solve', path: '/about-solve', Icon: Zap },
];

function SeaWolfProofSection({ onStart, checkingAccess }) {
  const [starting, setStarting] = useState(false);
  const video = getWalkthroughVideo('seaWolf2026');

  const handleStartSeaWolf = () => {
    onStart(() => setStarting(true));
  };

  return (
    <section id="seawolf-proof" className="py-20 sm:py-28 bg-white px-4 sm:px-8 md:px-12 lg:px-16">
      <div className="max-w-[120rem] mx-auto">
        <h2 className={sectionTitleClass + ' text-center mb-8 sm:mb-10'}>What your feedback actually shows</h2>
        <p className="text-center text-gray-600 max-w-2xl mx-auto mb-12 sm:mb-14 text-sm sm:text-base">
          Each run gives you something you can use on the next one: which checks passed, which trait or attribute failed, and the one change that would improve the run the most.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_0.9fr] gap-8 lg:gap-10 items-center">
          <div>
            <div className="rounded-2xl border border-slate-200/80 bg-white/95 shadow-xl overflow-hidden">
            <ProductDemoVideoEmbed videoKey="seaWolf2026" />
            <div className="px-4 py-2.5 border-t border-slate-100 bg-white">
              <div className="text-sm font-semibold text-gray-900">{video.title}</div>
              <div className="text-xs text-gray-500 mt-0.5">{video.description}</div>
            </div>
            </div>
          </div>
          <div className="space-y-3">
            {[
              { label: 'Checks passed', text: 'See exactly which site requirements your trio satisfied.' },
              { label: 'Trait or attribute gap', text: 'Spot the one trait or attribute gap that held the run back.' },
              { label: 'Coach insight', text: 'Understand why the logic worked or failed.' },
              { label: 'Best next move', text: 'Know the one change that would improve the next attempt most.' },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="flex gap-3 rounded-xl border border-slate-200/80 bg-white p-5 text-left"
              >
                <span className="text-sky-600 font-semibold text-base shrink-0">{item.label}:</span>
                <span className="text-gray-600 text-base">{item.text}</span>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.24 }}
              className="pt-2"
            >
              <button
                type="button"
                onClick={handleStartSeaWolf}
                disabled={starting || checkingAccess}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-400 px-5 py-3 text-sm font-semibold text-white shadow-md hover:shadow-lg transition w-full sm:w-auto justify-center disabled:opacity-80 disabled:pointer-events-none min-w-[180px]"
              >
                {starting || checkingAccess ? (
                  <>
                    <LoadingSpinner size="sm" tone="onPrimary" className="shrink-0" /> {checkingAccess ? 'Checking access…' : 'Starting…'}
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" strokeWidth={2} /> Start Sea Wolf Simulation
                  </>
                )}
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SeaWolfPreviewPanel() {
  return (
    <>
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.06)_0%,rgba(12,34,66,0.12)_100%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-300/60 to-transparent" />
      <div className="relative border-b border-sky-400/20 p-4 text-white bg-slate-900/40">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-sky-200/90">McKinsey Sea Wolf</span>
          <span className="rounded-full bg-sky-500/20 px-2.5 py-1 text-[11px] font-semibold text-sky-200">Simulation</span>
        </div>
      </div>
      <div className="relative p-4">
        <div className="rounded-2xl border border-white/15 overflow-hidden bg-white/5 backdrop-blur-sm ring-1 ring-white/5 shadow-[0_16px_36px_-24px_rgba(2,6,23,0.78)]">
          <img
            src="/images/landing/seawolf-in-session.png"
            alt="Sea Wolf in-session — characteristics and site profile"
            className="w-full h-auto block"
          />
        </div>
      </div>
    </>
  );
}

export default function SeaWolfPracticePage() {
  const navigate = useNavigate();
  const seaWolfAccess = usePremiumProductAccess(PREMIUM_PRODUCT_KEYS.seaWolfSimulation);

  const handleScrollToProof = () => document.getElementById('seawolf-proof')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const handleScrollToWalkthrough = () => document.getElementById('seawolf-proof')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const handleViewPricing = () => navigate('/', { state: { scrollToPricing: true } });
  const handleSeeDemo = () => navigate('/', { state: { scrollToLiveDemos: true, track: 'mck', demoVariantMck: 'assessment' } });
  const handleStartSeaWolf = (onAllowed) => {
    seaWolfAccess.attemptAccess(() => {
      onAllowed();
      navigate('/seawolf-app');
    }, {
      returnTo: '/seawolf-app',
      onDemo: handleSeeDemo,
    });
  };

  return (
    <>
      <SEO
        title="McKinsey Sea Wolf Simulation | Cognidoor"
        description="Run the McKinsey Sea Wolf simulation with real reps, clear logic, and feedback that teaches. Coach mode and exam-style reps for Solve assessment prep."
      />
      <div className="bg-white text-gray-900 font-sans" style={{ '--cog-blue-1': '#277AB2', '--cog-blue-2': '#0B5996', '--cog-blue-3': '#082B4A' }}>
        <LandingStyleHero
          track="mck"
          headline="Practise McKinsey Sea Wolf with coaching that explains the logic, not just the result."
          subheadline="Work through Sea Wolf-style scenarios, make the microbe-selection decisions yourself, and get post-run feedback that shows what passed, what failed, and what to change next time."
          eyebrowChips={['Coach mode + exam mode', 'Real decision reps', 'Feedback that teaches']}
          proofRow={[
            { value: 'Practise the logic, not just the answer' },
            { value: 'Coach mode and exam-style reps' },
            { value: 'Learn from every run' },
          ]}
          primaryCtaLabel="Start Sea Wolf Simulation"
          onPrimaryCta={handleScrollToProof}
          secondaryCtaLabel="Watch walkthrough"
          onSecondaryCta={handleScrollToWalkthrough}
          previewChildren={<SeaWolfPreviewPanel />}
        />

        <section className="py-10 sm:py-14 bg-gradient-to-b from-white via-slate-50/50 to-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-8 sm:mb-10'}>What you get from a Sea Wolf run</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whatYouGet.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={glassCardClass + ' text-left'}
              >
                <item.icon className="w-8 h-8 text-sky-600 mb-3" strokeWidth={1.8} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <SeaWolfProofSection
          onStart={handleStartSeaWolf}
          checkingAccess={seaWolfAccess.isLoading}
        />

        {/* CTA to Guide & Solver */}
        <section className="py-10 sm:py-12 px-4 sm:px-6 bg-gradient-to-r from-sky-50 to-blue-50/80 border-y border-slate-200/60">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-gray-700 text-sm sm:text-base mb-4">
              Want to check your trios or explore scenarios step-by-step? Use the Sea Wolf Guide & Solver.
            </p>
            <Link
              to="/seawolf"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors"
            >
              <BookOpen className="w-4 h-4" /> Sea Wolf Guide & Solver
            </Link>
          </div>
        </section>

        <section id="how-it-works" className="py-10 sm:py-14 bg-gradient-to-b from-white via-slate-50/50 to-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-8 sm:mb-10'}>How it works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {howItWorks.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={glassCardClass + ' text-left'}
              >
                <item.icon className="w-8 h-8 text-sky-600 mb-3" strokeWidth={1.8} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="py-10 sm:py-14 bg-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-8 sm:mb-10'}>Why this helps before Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whyThisHelps.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={glassCardClass + ' text-left'}
              >
                <item.icon className="w-8 h-8 text-sky-600 mb-3" strokeWidth={1.8} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="py-10 sm:py-14 bg-gradient-to-b from-white via-slate-50/30 to-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-6 sm:mb-8'}>Build your McKinsey prep stack</h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-6 text-sm">
            Sea Wolf Simulation is for assessment practice. Want to check your trios or explore scenarios? Use the Guide & Solver. The McKinsey coach is for live case practice.
          </p>
          <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
            {relatedLinks.map(({ label, path, Icon }) => (
              <motion.button
                key={path}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(path)}
                className="inline-flex items-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-gray-700 shadow-sm hover:border-sky-300 hover:bg-sky-50/50 hover:text-sky-800 transition-colors"
              >
                <Icon className="w-4 h-4" strokeWidth={2} /> {label}
              </motion.button>
            ))}
          </div>
          <p className="mt-6 text-sm text-gray-500">
            <button type="button" onClick={handleViewPricing} className="font-semibold text-sky-600 hover:text-sky-700 underline underline-offset-2">
              See plans and pricing
            </button>
          </p>
        </section>
      </div>
      <PremiumAccessPrompt {...seaWolfAccess.promptProps} />
    </>
  );
}
